import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Mail, MapPin, Phone, Send, Facebook, Instagram } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contact`, formData);
      toast({
        title: "Message Sent!",
        description: "Thank you for contacting us. We'll get back to you soon.",
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error sending your message. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-16 md:py-24" style={{ backgroundColor: '#e37c5520' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#7e2f22' }}>
              Contact Us
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              Have questions? Want to get involved? We'd love to hear from you!
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-5 gap-8">
              {/* Contact Info */}
              <div className="md:col-span-2 space-y-6">
                <div>
                  <h2 className="text-3xl font-bold mb-6" style={{ color: '#7e2f22' }}>Get In Touch</h2>
                  <p className="text-gray-600 leading-relaxed mb-8">
                    Whether you're interested in joining, mentoring, or supporting our work, we're here to answer your questions.
                  </p>
                </div>

                <Card className="border-0 shadow-lg">
                  <CardContent className="pt-6 space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#53a5ca20' }}>
                        <MapPin className="h-6 w-6" style={{ color: '#53a5ca' }} />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1" style={{ color: '#7e2f22' }}>Location</h3>
                        <p className="text-sm text-gray-600">Cherokee, North Carolina<br />Eastern Band of Cherokee Indians</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#e37c5520' }}>
                        <Mail className="h-6 w-6" style={{ color: '#e37c55' }} />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1" style={{ color: '#7e2f22' }}>Email</h3>
                        <a href="mailto:naiwadaughters@gmail.com" className="text-sm" style={{ color: '#53a5ca' }}>
                          naiwadaughters@gmail.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#dbbf5720' }}>
                        <Phone className="h-6 w-6" style={{ color: '#dbbf57' }} />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1" style={{ color: '#7e2f22' }}>Phone</h3>
                        <a href="tel:+18652028215" className="text-sm text-gray-600">(865)202-8215</a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div>
                  <h3 className="font-semibold mb-4" style={{ color: '#7e2f22' }}>Follow Us</h3>
                  <div className="flex space-x-3">
                    <a 
                      href="https://www.facebook.com/naiwadaughterscherokee" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 hover:opacity-80"
                      style={{ backgroundColor: '#53a5ca' }}
                    >
                      <Facebook className="h-5 w-5 text-white" />
                    </a>
                    <a 
                      href="https://www.instagram.com/naiwadaughters/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 hover:opacity-80"
                      style={{ backgroundColor: '#e37c55' }}
                    >
                      <Instagram className="h-5 w-5 text-white" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="md:col-span-3">
                <Card className="border-0 shadow-xl">
                  <CardHeader>
                    <CardTitle className="text-2xl" style={{ color: '#7e2f22' }}>Send Us A Message</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Full Name</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Your name"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email Address</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="your.email@example.com"
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="subject">Subject</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="What is this about?"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="message">Message</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us more..."
                          rows={6}
                          required
                        />
                      </div>
                      <Button 
                        type="submit" 
                        size="lg" 
                        className="w-full text-white hover:opacity-90 transition-all duration-200"
                        style={{ backgroundColor: '#53a5ca' }}
                      >
                        <Send className="mr-2 h-5 w-5" />
                        Send Message
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;